import { ref, computed, watch } from 'vue'

// Module-level so Day/Week/Month (and the Dashboard's team calendar) share one
// filter selection — switching views (picker, "1"/"2" shortcuts) mounts a
// different component, and per-instance state reset the filters every time.
// Teachers persist in localStorage (across reloads, as before); location in
// sessionStorage. No intersecting with the view's teacher list on restore:
// that list fills in lazily (lessons before roster), so trimming against it
// dropped selected teachers. filteredTeachers already ignores unknown names.
const TEACHERS_KEY = 'weekview-active-teachers'
const LOCATION_KEY = 'calendar-selected-location'
function readJson(storage, key, fallback) {
  try { return JSON.parse(storage.getItem(key)) ?? fallback } catch { return fallback }
}
const sharedTeachers = ref(new Set(readJson(localStorage, TEACHERS_KEY, [])))
const sharedLocation = ref(readJson(sessionStorage, LOCATION_KEY, null))
watch(sharedTeachers, ts => { try { localStorage.setItem(TEACHERS_KEY, JSON.stringify([...ts])) } catch {} })
watch(sharedLocation, loc => { try { sessionStorage.setItem(LOCATION_KEY, JSON.stringify(loc)) } catch {} })

// shared: false for a locked single-teacher calendar (no filter bar), so it
// neither applies nor overwrites the staff views' selection.
export function useCalendarFilters({ allTeachers, teacherMap, shared = true }) {
  const activeTeachers   = shared ? sharedTeachers : ref(new Set())
  const selectedLocation = shared ? sharedLocation : ref(null)  // null | '__none__' | location string

  // ── Teacher toggles ────────────────────────────────────────────────────────
  const toggleTeacher = t => {
    const next = new Set(activeTeachers.value)
    next.has(t) ? next.delete(t) : next.add(t)
    activeTeachers.value = next
  }

  const deselectAll = () => { activeTeachers.value = new Set() }

  // ── Instrument pills → select/deselect all teachers of that instrument ────
  const teachersForInstrument = inst =>
    allTeachers.value.filter(t => (teacherMap.value.get(t)?.instruments || []).includes(inst))

  const isInstrumentActive = inst => {
    const group = teachersForInstrument(inst)
    return group.length > 0 && group.every(t => activeTeachers.value.has(t))
  }

  const isInstrumentPartial = inst => {
    const group = teachersForInstrument(inst)
    return group.some(t => activeTeachers.value.has(t)) && !group.every(t => activeTeachers.value.has(t))
  }

  const toggleInstrument = inst => {
    const group = teachersForInstrument(inst)
    const allSelected = group.every(t => activeTeachers.value.has(t))
    const next = new Set(activeTeachers.value)
    if (allSelected) {
      group.forEach(t => next.delete(t))
    } else {
      group.forEach(t => next.add(t))
    }
    activeTeachers.value = next
  }

  // ── Location pills ─────────────────────────────────────────────────────────
  const selectLocation = loc => {
    selectedLocation.value = selectedLocation.value === loc ? null : loc
  }

  // A pure lesson filter — applies alongside teacher filtering in any view,
  // rather than swapping the grid to a location-only layout.
  const matchesLocation = lesson =>
    selectedLocation.value === null ||
    (lesson.location || '__none__') === selectedLocation.value

  // ── Derived: teachers to display (respects activeTeachers, not location) ──
  const filteredTeachers = computed(() =>
    activeTeachers.value.size === 0
      ? allTeachers.value
      : allTeachers.value.filter(t => activeTeachers.value.has(t))
  )

  return {
    activeTeachers, selectedLocation,
    toggleTeacher, deselectAll,
    toggleInstrument, isInstrumentActive, isInstrumentPartial,
    selectLocation, matchesLocation,
    filteredTeachers,
  }
}
